import { Button, Icon } from '@timelyapp/react';
import classNames from 'classnames';
import OnboardingHintPopover from 'components/popovers/OnboardingHintPopover';
import Popover from 'components/popovers/Popover';
import Tooltip from 'components/Tooltip';
import type { OnboardingHint } from 'constants/hints/onboarding';
import {
  AGENT_WIDGETS,
  WIDGET_AGENTS_DONUT,
  WIDGET_AGENTS_LINE,
  WIDGET_AGENTS_LIST,
  WIDGET_BILLED_VS_UNBILLED_DONUT,
  WIDGET_CLIENTS_DONUT,
  WIDGET_CLIENTS_HBAR,
  WIDGET_CLIENTS_LIST,
  WIDGET_HOURS_LINE,
  WIDGET_LABELS_DONUT,
  WIDGET_LABELS_HBAR,
  WIDGET_LINKED_WORK_DONUT,
  WIDGET_LINKED_WORK_LIST,
  WIDGET_PROJECTS_DONUT,
  WIDGET_TEAMS_DONUT,
  WIDGET_TEAMS_LIST,
  WIDGET_USERS_HBAR,
} from 'constants/widgets';
import { newReportWidgetFactory } from 'factories/superreports';
import React, { PureComponent } from 'react';
import { WidgetDataType, type WidgetDraft } from 'redux/modules/superreports.types';
import { objectUnionKeys } from 'utils/typesafe';
import styles from './WidgetAddButton.module.scss';
import { WidgetBarChartIcon, WidgetDonutChartIcon, WidgetTableIcon } from './WidgetIcons';

enum WidgetType {
  BarChart,
  DonutChart,
  Table,
}

const options = {
  Hours: [
    [WidgetType.BarChart, WIDGET_HOURS_LINE],
    [WidgetType.DonutChart, WIDGET_BILLED_VS_UNBILLED_DONUT],
  ],
  'Agent activity': [
    [WidgetType.BarChart, WIDGET_AGENTS_LINE],
    [WidgetType.DonutChart, WIDGET_AGENTS_DONUT],
    [WidgetType.Table, WIDGET_AGENTS_LIST],
  ],
  Clients: [
    [WidgetType.Table, WIDGET_CLIENTS_HBAR],
    [WidgetType.DonutChart, WIDGET_CLIENTS_DONUT],
  ],
  Projects: [
    [WidgetType.Table, WIDGET_CLIENTS_LIST],
    [WidgetType.DonutChart, WIDGET_PROJECTS_DONUT],
  ],
  People: [[WidgetType.Table, WIDGET_USERS_HBAR]],
  Tags: [
    [WidgetType.Table, WIDGET_LABELS_HBAR],
    [WidgetType.DonutChart, WIDGET_LABELS_DONUT],
  ],
  Teams: [
    [WidgetType.Table, WIDGET_TEAMS_LIST],
    [WidgetType.DonutChart, WIDGET_TEAMS_DONUT],
  ],
  'Linked Work': [
    [WidgetType.Table, WIDGET_LINKED_WORK_LIST],
    [WidgetType.DonutChart, WIDGET_LINKED_WORK_DONUT],
  ],
} as const;

interface Props {
  agentTrackingEnabled?: boolean;
  hintKey?: OnboardingHint;
  isHidden?: boolean;
  isLoading: boolean;
  referenceWidget?: WidgetDraft;
  showAddHint?: boolean;
  showByDefault?: boolean;
  widgetType?: string;
  flipId?: string;
  children?: React.ReactNode;

  onClick?: (widgetReference: WidgetDraft) => void;
  onOpen?: () => void;
  saveUserProperty?: (property: string) => unknown; // type from hints
}

interface State {
  showWidgets: boolean;
}

class WidgetAddButton extends PureComponent<Props, State> {
  state = {
    showWidgets: !!this.props.showByDefault,
  };

  handleToggleAvailableWidgets = () => {
    const showWidgets = !this.state.showWidgets;
    this.setState({ showWidgets });
    if (showWidgets) {
      this.props.onOpen?.();
    }
  };

  handleAddWidget = (widget: WidgetDraft) => {
    this.handleToggleAvailableWidgets();
    this.props.onClick?.(widget);
  };

  renderSubOption = (title: string, icon: React.ReactNode, dataType: string, group: string) => {
    const handleClick = () => {
      const newWidget = newReportWidgetFactory({
        widget_type: dataType,
        options: AGENT_WIDGETS.includes(dataType) ? undefined : this.props.referenceWidget?.options,
      });
      newWidget.sequence = this.props.referenceWidget?.sequence ?? 0;

      // Initialize default options for donut widgets if not present
      if (
        dataType.endsWith(':donut') &&
        !AGENT_WIDGETS.includes(dataType) &&
        !newWidget.options?.dataType
      ) {
        newWidget.options = {
          ...newWidget.options,
          dataType: WidgetDataType.Hours,
        };
      }

      this.handleAddWidget(newWidget);
    };

    return (
      <div className={styles.addWidget} key={dataType} data-testid={`render-sub-option-${title}`}>
        <Tooltip title={title}>
          <button aria-label={`${title} for ${group}`} onClick={handleClick}>
            {icon}
          </button>
        </Tooltip>
      </div>
    );
  };

  renderAvailableWidgets = (isLoading: boolean) => {
    const keys = objectUnionKeys(options).filter(
      option => option !== 'Agent activity' || this.props.agentTrackingEnabled
    );

    return (
      <ul className={classNames(styles.widgetList, { [styles.open]: this.state.showWidgets })}>
        {keys.map(option => (
          <li
            key={option}
            className={classNames({ [styles.disabled]: !options[option].length || isLoading })}
          >
            <span data-testid={`render-widget-${option}`}>{option}</span>
            {!isLoading && (
              <div className={styles.widgetPicker}>
                {options[option].map(([type, dataType]: readonly [WidgetType, string]) => {
                  switch (type) {
                    case WidgetType.Table:
                      return this.renderSubOption(
                        'Add table',
                        <WidgetTableIcon />,
                        dataType,
                        option
                      );
                    case WidgetType.BarChart:
                      return this.renderSubOption(
                        'Add bar chart',
                        <WidgetBarChartIcon />,
                        dataType,
                        option
                      );
                    case WidgetType.DonutChart:
                      return this.renderSubOption(
                        'Add donut chart',
                        <WidgetDonutChartIcon />,
                        dataType,
                        option
                      );
                    default:
                      return null;
                  }
                })}
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  renderWidgetHint = () => {
    if (!this.props.showAddHint || !this.props.hintKey || !this.props.saveUserProperty) return null;

    return (
      <div className={styles.hintContainer}>
        <OnboardingHintPopover
          position="bottom"
          hintKey={this.props.hintKey}
          isOpen
          onConfirm={this.props.saveUserProperty}
        />
      </div>
    );
  };

  render() {
    const {
      isLoading,
      isHidden,
      children,
      onClick,
      hintKey,
      referenceWidget,
      showAddHint,
      showByDefault,
      widgetType,
      onOpen,
      saveUserProperty,
      flipId,
      agentTrackingEnabled,
      ...rest
    } = this.props;
    const { showWidgets } = this.state;
    return (
      <div className={classNames(styles.wrapper, { [styles.open]: showWidgets })} {...rest}>
        <div className={classNames(styles.container, { [styles.hidden]: isHidden })}>
          {this.renderWidgetHint()}
          <Popover
            className={styles.popover}
            zIndex={1000}
            fixed
            arrowStyleModifier={() => {
              return {
                zIndex: -1,
              };
            }}
            targetWrapperElement="span"
            isOpen={showWidgets}
            ignoreTargetOnClick={false}
            closeOnClick
            target={
              <Button
                onClick={this.handleToggleAvailableWidgets}
                data-testid={showWidgets ? 'close-widget-button' : 'add-widget-button'}
                disabled={isLoading}
                priority="xhigh"
                size="small"
              >
                <Icon name="Plus" type="solid" />
                <span>Add charts and tables</span>
              </Button>
            }
          >
            {this.renderAvailableWidgets(isLoading)}
          </Popover>
        </div>
        {children}
      </div>
    );
  }
}

export default WidgetAddButton;
